import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/runtime';
import { categoryLabel } from '../i18n/categoryNames';
import { MessageCircle, Send, ChevronRight, Zap, Eye, Users, Wallet, Calendar, Clock, Wrench, MapPin, FileText, Sparkles } from 'lucide-react';
import { proposalAiAPI } from '../api/proposalAiAPI';
import { isNativeApp, isIOSApp, openAICreditsExternally } from '../utils/appBridge';
import { downloadAuthedFile } from '../utils/downloadFile';
import { viewCountQueryParam, markViewed } from '../utils/viewTracking';
import './ProjectDetailPage.css';

interface Project {
  id: number;
  user_id: number;
  client_nickname: string;
  client_profile_image?: string;
  category_id: number;
  category_name: string;
  category_icon: string;
  title: string;
  description: string;
  budget_min?: number;
  budget_max?: number;
  budget_type: string;
  deadline?: string;
  duration_days?: number;
  work_type: string;
  location?: string;
  required_skills?: string[];
  experience_level?: string;
  attachments?: Array<{name: string, url: string}>;
  status: string;
  proposals_count: number;
  views_count: number;
  is_urgent: boolean;
  created_at: string;
}

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, i18n } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // 제안서 모달
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [proposedBudget, setProposedBudget] = useState('');
  const [proposedDuration, setProposedDuration] = useState('');
  const [aiProposalLoading, setAiProposalLoading] = useState(false);
  const [aiProposalDraftId, setAiProposalDraftId] = useState<number | null>(null);
  const [aiProposalDownloading, setAiProposalDownloading] = useState(false);

  const API_BASE = API_BASE_URL;

  const fetchProjectDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/projects/${id}${viewCountQueryParam('project', id!)}`);
      const result = await response.json();
      if (result.success) { setProject(result.data); markViewed('project', id!); }
      else setError(result.message || t('projectDetail.loadFailed'));
    } catch (err) {
      console.error('[ProjectDetail] 프로젝트 상세 조회 오류:', err);
      setError(t('projectDetail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, API_BASE, t]);

  useEffect(() => {
    if (id) fetchProjectDetail();
  }, [id, fetchProjectDetail]);

  const formatBudget = () => {
    if (!project) return '';
    if (project.budget_type === 'negotiable') return t('projectDetail.negotiable');
    if (project.budget_type === 'hourly') return t('projectDetail.hourly', { v: project.budget_min?.toLocaleString() });
    if (project.budget_min && project.budget_max) {
      return t('projectDetail.budgetRange', { min: project.budget_min.toLocaleString(), max: project.budget_max.toLocaleString() });
    } else if (project.budget_min) return t('projectDetail.budgetMin', { v: project.budget_min.toLocaleString() });
    else if (project.budget_max) return t('projectDetail.budgetMax', { v: project.budget_max.toLocaleString() });
    return t('projectDetail.budgetTbd');
  };

  const formatDeadline = () => project?.deadline ? new Date(project.deadline).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US') : t('projectDetail.tbd');

  const getWorkTypeText = () => {
    const types: { [key: string]: string } = { 'remote': t('projects.workRemote'), 'onsite': t('projects.workOnsite'), 'hybrid': t('projects.workHybrid'), 'outsourcing': t('projectDetail.workOutsourcing') };
    return types[project?.work_type || 'remote'] || t('projects.workRemote');
  };

  const getExperienceLevelText = () => {
    const levels: { [key: string]: string } = { 'junior': t('projectDetail.expJunior'), 'intermediate': t('projectDetail.expIntermediate'), 'senior': t('projectDetail.expSenior'), 'any': t('projectDetail.expAny') };
    return levels[project?.experience_level || 'any'] || t('projectDetail.expAny');
  };

  const handleProposalClick = () => {
    if (!user) { alert(t('common.loginRequired')); navigate('/login'); return; }
    if (user.nickname === project?.client_nickname) { alert(t('projectDetail.ownProjectProposal')); return; }
    if (project?.status !== 'open') { alert(t('projectDetail.onlyOpen')); return; }
    setShowProposalModal(true);
  };

  // 제안서 보내기 → 채팅으로 전송
  const handleProposalSubmit = async () => {
    if (!coverLetter.trim()) { alert(t('projectDetail.enterProposal')); return; }
    if (coverLetter.length < 50) { alert(t('projectDetail.minChars')); return; }

    try {
      setProposalLoading(true);
      console.log('[Proposal] 제안서 전송 시작:', { projectId: id, coverLetter: coverLetter.substring(0, 50) });
      
      const response = await fetch(`${API_BASE}/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          project_id: id,
          proposal: coverLetter,
          amount: proposedBudget ? parseInt(proposedBudget.replace(/,/g, '')) : null,
          delivery_time: proposedDuration ? parseInt(proposedDuration) : 7
        })
      });

      const result = await response.json();
      console.log('[Proposal] 제안서 전송 결과:', result);

      if (result.success) {
        alert(t('projectDetail.proposalSent'));
        setShowProposalModal(false);
        setCoverLetter(''); setProposedBudget(''); setProposedDuration('');
        // 채팅방으로 이동
        navigate(`/chat/${result.data.roomId}`);
      } else {
        alert(result.message || t('projectDetail.proposalFailed'));
      }
    } catch (err) {
      console.error('[Proposal] 제안서 전송 오류:', err);
      alert(t('projectDetail.proposalError'));
    } finally {
      setProposalLoading(false);
    }
  };

  // AI 제안서 도우미 (tech_spec.md 8-2절) — 건당 490원 / 12건 4,990원
  const handleGenerateAIProposal = async () => {
    if (!project) return;
    setAiProposalLoading(true);
    try {
      const res = await proposalAiAPI.generate(project.id, i18n.language.startsWith('en') ? 'en' : 'ko');
      if (!res.success) {
        if (res.code === 'NO_CREDIT') {
          // iOS 앱: 결제 수단 언급·외부 브라우저 이동 없이 안내만 (AICreditGate.tsx 참조,
          // Apple 심사 가이드라인 3.1.1 대응, 2026-08 결정)
          if (isIOSApp()) { alert('이용권이 부족합니다.'); }
          else if (isNativeApp()) { await openAICreditsExternally('proposal'); }
          else { navigate('/ai-credits/proposal'); }
          return;
        }
        alert(res.message || 'AI 제안서 생성에 실패했습니다.');
        return;
      }
      const d = res.data!.draft;
      const composed = [
        d.opening,
        '',
        ...d.fitReasons.map(f => `• ${f.requirement}: ${f.matchedExperience} — ${f.why}`),
        d.proposedPlan.length ? '\n[진행 계획]' : '',
        ...d.proposedPlan.map((p, i) => `${i + 1}. ${p}`),
        d.portfolioHighlight ? `\n[참고 포트폴리오] ${d.portfolioHighlight}` : '',
        '',
        d.closing,
      ].filter((line) => line !== '').join('\n');
      setCoverLetter(composed);
      setAiProposalDraftId(res.data!.draftId);
    } catch (err: any) {
      console.error('[ProposalAI] 생성 오류:', err);
      alert(err.response?.data?.message || 'AI 제안서 생성 중 오류가 발생했습니다.');
    } finally {
      setAiProposalLoading(false);
    }
  };

  const handleDownloadAIProposal = async () => {
    if (!aiProposalDraftId) return;
    setAiProposalDownloading(true);
    try {
      await downloadAuthedFile(`/api/proposal-ai/download/${aiProposalDraftId}`, `AI-proposal-${project?.id}.docx`);
    } catch (err: any) {
      alert(err.message || '다운로드 중 오류가 발생했습니다.');
    } finally {
      setAiProposalDownloading(false);
    }
  };

  const handleContactClient = async () => {
    if (!user) { alert(t('common.loginRequired')); navigate('/login'); return; }
    if (user.nickname === project?.client_nickname) { alert(t('projectDetail.ownProject')); return; }

    try {
      setChatLoading(true);
      console.log('[Chat] 채팅방 생성 요청:', { projectId: id });
      
      const response = await fetch(`${API_BASE}/chat/room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ projectId: id })
      });

      const result = await response.json();
      console.log('[Chat] 채팅방 생성 결과:', result);
      
      if (result.success) navigate(`/chat/${result.room.id}`);
      else alert(result.message || t('serviceDetail.chatCreateFailed'));
    } catch (error) {
      console.error('[Chat] 채팅 시작 오류:', error);
      alert(t('serviceDetail.chatCreateError'));
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) return <div className="loading-container">{t('projectDetail.loading')}</div>;
  if (error || !project) return (
    <div className="error-container">
      <h2>{t('projectDetail.errorTitle')}</h2>
      <p>{error || t('projectDetail.notFound')}</p>
      <button onClick={() => navigate('/projects')}>{t('projectDetail.backToList')}</button>
    </div>
  );

  const isOwner = user && user.nickname === project.client_nickname;

  return (
    <div className="project-detail-page">
      <div className="project-detail-container">
        <div className="breadcrumb">
          <span onClick={() => navigate('/projects')}>{t('projectDetail.breadcrumb')}</span>
          <ChevronRight size={13} style={{ margin: '0 2px', color: '#CCC' }} />
          <span>{categoryLabel(project.category_name)}</span>
        </div>

        {project.is_urgent && (
          <div className="urgent-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Zap size={13} />{t('projectDetail.urgent')}
          </div>
        )}
        <h1 className="project-title">{project.title}</h1>

        <div className="client-info">
          <div className="client-avatar">
            {project.client_profile_image ? (
              <img src={project.client_profile_image} alt={project.client_nickname} />
            ) : (
              <div className="avatar-placeholder">{project.client_nickname?.charAt(0).toUpperCase()}</div>
            )}
          </div>
          <div>
            <div className="client-name">{project.client_nickname}</div>
            <div className="project-meta-info" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>{categoryLabel(project.category_name)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Eye size={12} /> {project.views_count}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Users size={12} /> {t('projects.proposals')} {project.proposals_count}</span>
            </div>
          </div>
        </div>

        <div className="project-info-grid">
          <div className="info-card"><div className="info-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Wallet size={13} />{t('projects.budget')}</div><div className="info-value">{formatBudget()}</div></div>
          <div className="info-card"><div className="info-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={13} />{t('projects.deadline')}</div><div className="info-value">{formatDeadline()}</div></div>
          <div className="info-card"><div className="info-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={13} />{t('projectDetail.duration')}</div><div className="info-value">{project.duration_days ? t('projectDetail.daysN', { n: project.duration_days }) : t('projectDetail.tbd')}</div></div>
          <div className="info-card"><div className="info-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Wrench size={13} />{t('projectDetail.workType')}</div><div className="info-value">{getWorkTypeText()}</div></div>
          <div className="info-card"><div className="info-label">{t('projectDetail.expLevel')}</div><div className="info-value">{getExperienceLevelText()}</div></div>
          {project.location && <div className="info-card"><div className="info-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={13} />{t('projectDetail.location')}</div><div className="info-value">{project.location}</div></div>}
        </div>

        <div className="project-description">
          <h2>{t('projectDetail.description')}</h2>
          <p>{project.description}</p>
        </div>

        {project.required_skills && project.required_skills.length > 0 && (
          <div className="required-skills">
            <h2>{t('projectDetail.requiredSkills')}</h2>
            <div className="skills-list">
              {project.required_skills.map((skill, index) => <span key={index} className="skill-tag">{skill}</span>)}
            </div>
          </div>
        )}

        {project.attachments && project.attachments.length > 0 && (
          <div className="attachments">
            <h2>{t('projectDetail.attachments')}</h2>
            <div className="attachment-list">
              {project.attachments.map((file, index) => (
                <a key={index} href={file.url} target="_blank" rel="noopener noreferrer" className="attachment-item">
                  <FileText size={14} style={{ marginRight: 4 }} />{file.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {!isOwner && (
          <div className="action-buttons">
            <button className="btn-propose" onClick={handleProposalClick} disabled={project.status !== 'open'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Send size={15} />{t('projectDetail.sendProposal')}
            </button>
            <button className="btn-contact" onClick={handleContactClient} disabled={chatLoading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <MessageCircle size={15} />{chatLoading ? t('serviceDetail.creatingChat') : t('projectDetail.chat')}
            </button>
          </div>
        )}

        {isOwner && (
          <div className="action-buttons">
            <button className="btn-edit" onClick={() => navigate(`/projects/${project.id}/edit`)}>{t('projectDetail.edit')}</button>
          </div>
        )}
      </div>

      {showProposalModal && (
        <div className="modal-overlay" onClick={() => setShowProposalModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Send size={18} />{t('projectDetail.sendProposal')}
            </h2>

            <div className="proposal-project-info">
              <strong>{project.title}</strong>
              <span>{t('projects.budget')}: {formatBudget()}</span>
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ margin: 0 }}>{t('projectDetail.coverLabel')}</label>
                <button
                  type="button"
                  onClick={handleGenerateAIProposal}
                  disabled={aiProposalLoading}
                  title="공고 내용과 내 프로필을 바탕으로 AI가 제안서 초안을 작성합니다."
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '6px 10px',
                    borderRadius: 8, border: '1px solid #a78bfa', background: 'transparent', color: '#a78bfa',
                    cursor: aiProposalLoading ? 'not-allowed' : 'pointer', fontWeight: 600,
                  }}
                >
                  <Sparkles size={13} />{aiProposalLoading ? '생성 중...' : 'AI 제안서 초안 (건당 490원)'}
                </button>
              </div>
              <textarea
                value={coverLetter}
                onChange={e => setCoverLetter(e.target.value)}
                placeholder={t('projectDetail.coverPlaceholder')}
                rows={8}
              />
              <span className="char-count">{t('projectDetail.charsN', { n: coverLetter.length })}</span>
              {aiProposalDraftId && (
                <button
                  type="button"
                  onClick={handleDownloadAIProposal}
                  disabled={aiProposalDownloading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '6px 10px', marginTop: 6,
                    borderRadius: 8, border: '1px solid #a78bfa', background: 'transparent', color: '#a78bfa',
                    cursor: aiProposalDownloading ? 'not-allowed' : 'pointer', fontWeight: 600,
                  }}
                >
                  {aiProposalDownloading ? '다운로드 중...' : '이 AI 제안서 워드 파일로 다운로드'}
                </button>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('projectDetail.bidLabel')}</label>
                <input
                  type="text"
                  value={proposedBudget}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    setProposedBudget(val ? parseInt(val).toLocaleString() : '');
                  }}
                  placeholder={t('projectDetail.bidPlaceholder')}
                />
              </div>
              <div className="form-group">
                <label>{t('projectDetail.durationLabel')}</label>
                <input
                  type="number"
                  value={proposedDuration}
                  onChange={e => setProposedDuration(e.target.value)}
                  placeholder={t('projectDetail.durationPlaceholder')}
                  min="1"
                />
              </div>
            </div>

            <p className="modal-notice" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <MessageCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              {t('projectDetail.modalNotice')}
            </p>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowProposalModal(false)}>{t('common.cancel')}</button>
              <button className="submit-btn" onClick={handleProposalSubmit} disabled={proposalLoading || coverLetter.length < 50}>
                {proposalLoading ? t('projectDetail.sending') : t('projectDetail.sendProposal')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailPage;
